import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../../api/supabase'
import AllocationDialog from './AllocationDialog'
import { allocatedNet, deleteAllocation, r2, WARNING_LABEL, type SupplierInvoice } from '../../lib/supplierInvoices'

interface Alloc { id: string; amount: number; amount_gbp: number; notes: string | null; created_at: string; job: { job_ref: string } | null; charge: { description: string | null; charge_code_id: string | null } | null }
interface Ack { id: string; warning_type: string; warning_key: string; reason: string | null; acked_by_email: string | null; acked_at: string }

const STATUS_STYLE: Record<string, string> = {
  UNALLOCATED: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
  PARTIAL: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  ALLOCATED: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
}
export const StatusPill = ({ status }: { status: string }) =>
  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[status] ?? STATUS_STYLE.UNALLOCATED}`}>{status}</span>

interface Props { invoiceId: string; onClose: () => void; onChanged: () => void }

export default function SupplierInvoiceDetail({ invoiceId, onClose, onChanged }: Props) {
  const [inv, setInv] = useState<SupplierInvoice | null>(null)
  const [allocs, setAllocs] = useState<Alloc[]>([])
  const [acks, setAcks] = useState<Ack[]>([])
  const [codes, setCodes] = useState<Record<string, string>>({})
  const [showAlloc, setShowAlloc] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    const [i, a, k, c] = await Promise.all([
      supabase.from('supplier_invoices').select('*, supplier:customers!supplier_id(id,code,name), allocations:supplier_invoice_allocations(amount)').eq('id', invoiceId).single(),
      supabase.from('supplier_invoice_allocations').select('id, amount, amount_gbp, notes, created_at, job:jobs(job_ref), charge:job_charges(description, charge_code_id)').eq('supplier_invoice_id', invoiceId).order('created_at'),
      supabase.from('supplier_invoice_warning_acks').select('id, warning_type, warning_key, reason, acked_by_email, acked_at').eq('supplier_invoice_id', invoiceId).order('acked_at', { ascending: false }),
      supabase.from('charge_codes').select('id, code'),
    ])
    if (i.error) { setError(i.error.message); return }
    setInv(i.data as unknown as SupplierInvoice)
    setAllocs((a.data ?? []) as unknown as Alloc[])
    setAcks((k.data ?? []) as Ack[])
    setCodes(Object.fromEntries((c.data ?? []).map((x: { id: string; code: string }) => [x.id, x.code])))
  }, [invoiceId])
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { load() }, [load])

  async function openAttachment() {
    if (!inv?.attachment_path) return
    const { data, error: e } = await supabase.storage.from('supplier-invoices').createSignedUrl(inv.attachment_path, 120)
    if (e || !data) { setError(e?.message ?? 'Could not open attachment'); return }
    window.open(data.signedUrl, '_blank', 'noopener')
  }

  async function handleDelete(a: Alloc) {
    if (!confirm(`Remove the ${a.amount} allocation to ${a.job?.job_ref ?? 'this job'}? The cost line's actual will be recalculated.`)) return
    try {
      const res = await deleteAllocation(a.id)
      if (!res.ok) { setError(res.error); return }
      await load(); onChanged()
    } catch (e) { setError(e instanceof Error ? e.message : String(e)) }
  }

  if (!inv) return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 text-white">{error ?? 'Loading…'}</div>
  const allocated = allocatedNet({ allocations: allocs })
  const unallocated = r2(inv.net_amount - allocated)
  const th = 'px-3 py-2 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400'
  const td = 'px-3 py-2 text-sm text-gray-800 dark:text-gray-200'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
      <div className="flex max-h-[92vh] w-full max-w-4xl flex-col rounded-xl bg-white shadow-xl dark:bg-gray-800">
        <div className="flex items-start justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{inv.supplier?.name ?? 'Supplier'} · {inv.invoice_no}</h2>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {inv.invoice_date} · net {inv.net_amount.toFixed(2)} + VAT {inv.vat_amount.toFixed(2)} = {inv.total_amount.toFixed(2)} {inv.currency}
              {inv.currency !== 'GBP' && ` (1 ${inv.currency} = ${inv.exch_rate} GBP)`}
            </p>
            <p className="mt-1 text-sm text-gray-700 dark:text-gray-200">
              <StatusPill status={inv.status} /> <span className="ml-2">Allocated {allocated.toFixed(2)} / Unallocated {unallocated.toFixed(2)} {inv.currency} <span className="text-xs text-gray-500">(both net of VAT)</span></span>
            </p>
            {inv.notes && <p className="mt-1 text-xs text-gray-500">{inv.notes}</p>}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 dark:hover:text-white" aria-label="Close">✕</button>
        </div>

        <div className="space-y-6 overflow-y-auto px-6 py-4">
          {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
          <div className="flex gap-2">
            <button onClick={() => setShowAlloc(true)} disabled={unallocated <= 0.005}
              title={unallocated <= 0.005 ? 'Fully allocated' : undefined}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">Allocate to job costs</button>
            {inv.attachment_path && <button onClick={openAttachment} className="rounded-lg border border-gray-300 px-4 py-2 text-sm dark:border-gray-600 dark:text-gray-200">View attachment</button>}
          </div>

          <section>
            <h3 className="mb-2 text-sm font-semibold text-gray-900 dark:text-white">Allocations</h3>
            {allocs.length === 0 ? <p className="text-sm text-gray-500">Nothing allocated yet.</p> : (
              <table className="w-full">
                <thead><tr><th className={th}>Job</th><th className={th}>Cost line</th><th className={th}>Net ({inv.currency})</th><th className={th}>GBP</th><th className={th}>Notes</th><th /></tr></thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {allocs.map(a => (
                    <tr key={a.id}>
                      <td className={td}>{a.job?.job_ref ?? '—'}</td>
                      <td className={td}>{(a.charge?.charge_code_id && codes[a.charge.charge_code_id]) || '—'} {a.charge?.description ?? ''}</td>
                      <td className={td}>{Number(a.amount).toFixed(2)}</td>
                      <td className={td}>{Number(a.amount_gbp).toFixed(2)}</td>
                      <td className={td}>{a.notes ?? ''}</td>
                      <td className={td}><button onClick={() => handleDelete(a)} className="text-xs text-red-600 hover:underline">Remove</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <section>
            <h3 className="mb-2 text-sm font-semibold text-gray-900 dark:text-white">Warning log <span className="font-normal text-gray-500">(warnings that were overridden &mdash; permanent record)</span></h3>
            {acks.length === 0 ? <p className="text-sm text-gray-500">No warnings were overridden for this invoice.</p> : (
              <ul className="space-y-1 text-sm">
                {acks.map(k => (
                  <li key={k.id} className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-100">
                    <span className="font-medium">{WARNING_LABEL[k.warning_type] ?? k.warning_type}</span>
                    {' — '}{k.acked_by_email ?? 'unknown'} · {new Date(k.acked_at).toLocaleString('en-GB')}
                    {k.reason ? <span className="block text-xs">Reason: {k.reason}</span> : <span className="block text-xs italic opacity-70">No reason given</span>}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>
      {showAlloc && <AllocationDialog invoice={{ ...inv, allocations: allocs }} onClose={() => setShowAlloc(false)} onSaved={() => { setShowAlloc(false); load(); onChanged() }} />}
    </div>
  )
}
